"use client";

import { useCallback, useEffect, useMemo, useState } from "react";

import { Asset, BusyAction, Folder, TreeNode } from "./types";
import { buildAssetUrl, matchesAccept } from "./utils";

async function apiJson<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
  const res = await fetch(input, init);
  const data = await res.json();
  if (!res.ok)
    throw new Error(data?.message || `Request failed (${res.status})`);
  return data;
}

function uploadForm<T>(
  input: string,
  body: FormData,
  onProgress: (progress: number) => void,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", input);
    xhr.responseType = "json";
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0)
        onProgress(Math.min(1, event.loaded / event.total));
    };
    xhr.onerror = () => reject(new Error("Upload failed"));
    xhr.onabort = () => reject(new Error("Upload cancelled"));
    xhr.onload = () => {
      const data = xhr.response;
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(
          new Error(data?.message || `Request failed (${xhr.status || 0})`),
        );
        return;
      }
      onProgress(1);
      resolve(data as T);
    };
    xhr.send(body);
  });
}

type SelectionHelpers = {
  selectedFolderIds: string[];
  selectedAssetIds: string[];
  selectedAsset: Asset | null;
  clearSelection: () => void;
  selectSingleFolder: (id: string) => void;
  selectSingleAsset: (id: string) => void;
  toggleFolder: (id: string, checked: boolean) => void;
  toggleAsset: (id: string, checked: boolean) => void;
  setSelectionForContext: (type: "folder" | "asset", id: string) => void;
};

type MediaManagerState = {
  currentFolder: Folder | null;
  childFolders: Folder[];
  assets: Asset[];
  filteredAssets: Asset[];
  tree: Record<string, TreeNode>;
  rootId: string | null;
  breadcrumbs: string[];
  includeTrashed: boolean;
  loadingFolders: boolean;
  loadingAssets: boolean;
  uploading: boolean;
  uploadProgress: number | null;
  busyAction: BusyAction;
  viewMode: "list" | "grid";
  message: string | null;
  contextMenu: {
    x: number;
    y: number;
    type: "folder" | "asset";
    id: string;
  } | null;
  pendingMove: { folders: string[]; assets: string[] } | null;
  inlineFolderName: string | null;
  selectedFolderIds: string[];
  selectedAssetIds: string[];
  selectedAsset: Asset | null;
};

type MediaManagerActions = {
  goToFolder: (folderId?: string | null) => Promise<void>;
  loadAssets: (folderId?: string | null) => Promise<void>;
  createFolder: (name: string) => Promise<void>;
  deleteFolder: (folderId: string) => Promise<void>;
  renameFolder: (folderId: string, name: string) => Promise<void>;
  moveFolderToRoot: (folderId: string) => Promise<void>;
  renameAsset: (assetId: string, name: string) => Promise<void>;
  updateAssetStatus: (assetId: string, status: string) => Promise<void>;
  handleDirectUpload: (
    file: File,
    options?: {
      folderId?: string | null;
      variantOfId?: string | null;
      status?: string;
      successMessage?: string;
    },
  ) => Promise<Asset | null>;
  handleDroppedFiles: (files: FileList | File[]) => Promise<Asset[]>;
  createPresetVariants: (
    assetId: string,
    presetId: string,
  ) => Promise<Asset | null>;
  handlePasteMove: () => Promise<void>;
  toggleNode: (folder: Folder) => void;
  loadTreeNode: (folder: Folder) => Promise<void>;
  openAsset: (asset: Asset | null) => void;
  downloadAsset: (asset: Asset | null) => void;
  setContextMenu: (ctx: MediaManagerState["contextMenu"]) => void;
  closeContext: () => void;
  setIncludeTrashed: (val: boolean) => void;
  setViewMode: (mode: "list" | "grid") => void;
  setInlineFolderName: (val: string | null) => void;
  setPendingMove: (val: MediaManagerState["pendingMove"]) => void;
  setMessage: (val: string | null) => void;
  selection: SelectionHelpers;
  getFolderById: (id: string) => Folder | undefined;
  getAssetById: (id: string) => Asset | undefined;
};

function useSelection(getAssetById: (id: string) => Asset | undefined) {
  const [selectedFolderIds, setSelectedFolderIds] = useState<string[]>([]);
  const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([]);
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);

  const clearSelection = useCallback(() => {
    setSelectedFolderIds([]);
    setSelectedAssetIds([]);
    setSelectedAssetId(null);
  }, []);

  const selectSingleFolder = useCallback((id: string) => {
    setSelectedFolderIds([id]);
    setSelectedAssetIds([]);
    setSelectedAssetId(null);
  }, []);

  const selectSingleAsset = useCallback((id: string) => {
    setSelectedAssetIds([id]);
    setSelectedFolderIds([]);
    setSelectedAssetId(id);
  }, []);

  const toggleFolder = useCallback((id: string, checked: boolean) => {
    setSelectedFolderIds((prev) => {
      const set = new Set(prev);
      if (checked) set.add(id);
      else set.delete(id);
      return Array.from(set);
    });
    if (checked) {
      setSelectedAssetIds([]);
      setSelectedAssetId(null);
    }
  }, []);

  const toggleAsset = useCallback((id: string, checked: boolean) => {
    setSelectedAssetIds((prev) => {
      const set = new Set(prev);
      if (checked) set.add(id);
      else set.delete(id);
      return Array.from(set);
    });
    if (checked) {
      setSelectedFolderIds([]);
      setSelectedAssetId(id);
    } else {
      setSelectedAssetId((prev) => (prev === id ? null : prev));
    }
  }, []);

  const setSelectionForContext = useCallback(
    (type: "folder" | "asset", id: string) => {
      if (type === "asset") {
        const asset = getAssetById(id);
        if (asset) setSelectedAssetId(asset.id);
        setSelectedAssetIds([id]);
        setSelectedFolderIds([]);
      } else {
        setSelectedFolderIds([id]);
        setSelectedAssetIds([]);
        setSelectedAssetId(null);
      }
    },
    [getAssetById],
  );

  const selectedAsset = useMemo(
    () => (selectedAssetId ? (getAssetById(selectedAssetId) ?? null) : null),
    [getAssetById, selectedAssetId],
  );

  return {
    selectedFolderIds,
    selectedAssetIds,
    selectedAsset,
    clearSelection,
    selectSingleFolder,
    selectSingleAsset,
    toggleFolder,
    toggleAsset,
    setSelectionForContext,
  } satisfies SelectionHelpers;
}

type MediaManagerOptions = {
  accept?: string[];
};

export function useMediaManager(options?: MediaManagerOptions): {
  state: MediaManagerState;
  actions: MediaManagerActions;
} {
  const accept = useMemo(() => options?.accept ?? [], [options?.accept]);
  const [currentFolder, setCurrentFolder] = useState<Folder | null>(null);
  const [tree, setTree] = useState<Record<string, TreeNode>>({});
  const [rootId, setRootId] = useState<string | null>(null);
  const [includeTrashed, setIncludeTrashed] = useState(false);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [busyAction, setBusyAction] = useState<BusyAction>(null);
  const [viewMode, setViewMode] = useState<"list" | "grid">("list");
  const [message, setMessage] = useState<string | null>(null);
  const [contextMenu, setContextMenu] =
    useState<MediaManagerState["contextMenu"]>(null);
  const [pendingMove, setPendingMove] =
    useState<MediaManagerState["pendingMove"]>(null);
  const [inlineFolderName, setInlineFolderName] = useState<string | null>(null);

  const getFolderById = useCallback(
    (id: string) => {
      const node = tree[id];
      return node?.folder;
    },
    [tree],
  );

  const getAssetById = useCallback(
    (id: string) => {
      for (const node of Object.values(tree)) {
        const found = node.assets.find((asset) => asset.id === id);
        if (found) return found;
      }
      return undefined;
    },
    [tree],
  );

  const selection = useSelection(getAssetById);
  const { clearSelection } = selection;

  const assets = useMemo(() => {
    if (!currentFolder?.id) return [] as Asset[];
    return tree[currentFolder.id]?.assets ?? [];
  }, [currentFolder?.id, tree]);

  const filteredAssets = useMemo(
    () => assets.filter((asset) => matchesAccept(asset, accept)),
    [assets, accept],
  );

  const childFolders = useMemo(() => {
    if (!currentFolder?.id) return [] as Folder[];
    const node = tree[currentFolder.id];
    if (!node) return [] as Folder[];
    return node.childrenIds
      .map((id) => tree[id]?.folder)
      .filter(Boolean) as Folder[];
  }, [currentFolder?.id, tree]);

  const breadcrumbs = useMemo(() => {
    const path = currentFolder?.path ?? "root";
    return path.split("/").filter(Boolean);
  }, [currentFolder]);

  const runAction = useCallback(
    async <T>(
      label: BusyAction,
      fn: () => Promise<T>,
      successMessage?: string,
    ): Promise<{ ok: true; result: T } | { ok: false; error: any }> => {
      setBusyAction((prev) => prev ?? label);
      try {
        const result = await fn();
        if (successMessage) setMessage(successMessage);
        return { ok: true, result };
      } catch (err: any) {
        setMessage(err?.message || "Request failed");
        return { ok: false, error: err };
      } finally {
        setBusyAction((prev) => (prev === label ? null : prev));
      }
    },
    [],
  );

  const goToFolder = useCallback(
    async (parentId?: string | null) => {
      setLoadingFolders(true);
      try {
        const url = parentId
          ? `/api/media/folders?parentId=${parentId}`
          : "/api/media/folders";
        const data = await apiJson<{ parent: Folder; children: Folder[] }>(url);
        setCurrentFolder(data.parent);
        if (!rootId) setRootId(data.parent.id);
        setTree((prev) => {
          const next: Record<string, TreeNode> = { ...prev };
          const existingParent = next[data.parent.id];
          next[data.parent.id] = {
            folder: existingParent?.folder.previewAsset
              ? {
                  ...data.parent,
                  previewAsset: existingParent.folder.previewAsset,
                }
              : data.parent,
            childrenIds: data.children.map((c) => c.id),
            assets: existingParent?.assets ?? [],
            open: existingParent?.open ?? true,
            loaded: existingParent?.loaded ?? false,
            loading: existingParent?.loading ?? false,
          };
          data.children.forEach((child) => {
            const existing = next[child.id];
            next[child.id] = {
              folder: child,
              childrenIds: existing?.childrenIds ?? [],
              assets: existing?.assets ?? [],
              open: existing?.open ?? false,
              loaded: existing?.loaded ?? false,
              loading: existing?.loading ?? false,
            };
          });
          return next;
        });
      } catch (err: any) {
        setMessage(err?.message || "Failed to load folders");
      } finally {
        setLoadingFolders(false);
      }
    },
    [rootId],
  );

  const loadAssets = useCallback(
    async (folderId?: string | null) => {
      const targetId = folderId ?? currentFolder?.id;
      if (!targetId) return;
      setLoadingAssets(true);
      try {
        const params = new URLSearchParams();
        params.set("folderId", targetId);
        if (includeTrashed) params.set("includeTrashed", "true");
        const data = await apiJson<{ assets: Asset[] }>(
          `/api/media/assets?${params.toString()}`,
        );
        setTree((prev) => {
          const existing = prev[targetId];
          if (!existing) return prev;
          return {
            ...prev,
            [targetId]: {
              ...existing,
              assets: data.assets ?? [],
              loaded: true,
              loading: false,
            },
          };
        });
      } catch (err: any) {
        setMessage(err?.message || "Failed to load assets");
      } finally {
        setLoadingAssets(false);
      }
    },
    [currentFolder?.id, includeTrashed],
  );

  const createFolder = useCallback(
    async (name: string) => {
      const trimmed = name.trim();
      if (!trimmed) return;
      await runAction(
        null,
        async () => {
          await apiJson("/api/media/folders", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              name: trimmed,
              parentId: currentFolder?.id,
            }),
          });
          setInlineFolderName(null);
          await goToFolder(currentFolder?.id);
        },
        "Folder created",
      );
    },
    [currentFolder?.id, goToFolder, runAction],
  );

  const deleteFolder = useCallback(
    async (folderId: string) => {
      await runAction(
        "delete",
        async () => {
          await apiJson(`/api/media/folders/${folderId}`, { method: "DELETE" });
          await goToFolder(currentFolder?.id);
        },
        "Folder deleted",
      );
    },
    [currentFolder?.id, goToFolder, runAction],
  );

  const renameFolder = useCallback(
    async (folderId: string, name: string) => {
      const trimmed = name.trim();
      if (!trimmed) return;
      await runAction(
        null,
        async () => {
          await apiJson(`/api/media/folders/${folderId}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ name: trimmed }),
          });
          await goToFolder(currentFolder?.id);
        },
        "Folder renamed",
      );
    },
    [currentFolder?.id, goToFolder, runAction],
  );

  const moveFolderToRoot = useCallback(
    async (folderId: string) => {
      await runAction(
        null,
        async () => {
          await apiJson(`/api/media/folders/${folderId}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ parentId: null }),
          });
          await goToFolder(currentFolder?.id);
        },
        "Folder moved",
      );
    },
    [currentFolder?.id, goToFolder, runAction],
  );

  const renameAsset = useCallback(
    async (assetId: string, name: string) => {
      const trimmed = name.trim();
      if (!trimmed) return;
      await runAction(
        null,
        async () => {
          await apiJson(`/api/media/assets/${assetId}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ name: trimmed }),
          });
          await loadAssets();
        },
        "Asset renamed",
      );
    },
    [loadAssets, runAction],
  );

  const updateAssetStatus = useCallback(
    async (assetId: string, status: string) => {
      await runAction(
        "delete",
        async () => {
          await apiJson(`/api/media/assets/${assetId}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ status }),
          });
          await loadAssets();
        },
        `Status -> ${status}`,
      );
    },
    [loadAssets, runAction],
  );

  const handleDirectUpload = useCallback(
    async (
      file: File,
      options?: {
        folderId?: string | null;
        variantOfId?: string | null;
        status?: string;
        successMessage?: string;
        onProgress?: (progress: number) => void;
      },
    ) => {
      const managesProgress = !options?.onProgress;
      if (managesProgress) {
        setUploading(true);
        setUploadProgress(0);
      }
      try {
        const result = await runAction(
          "upload",
          async () => {
            const fd = new FormData();
            fd.append("file", file);
            const targetFolderId =
              options?.folderId === undefined
                ? currentFolder?.id
                : options.folderId;
            if (targetFolderId) fd.append("folderId", targetFolderId);
            if (options?.variantOfId)
              fd.append("variantOfId", options.variantOfId);
            if (options?.status) fd.append("status", options.status);
            const data = await uploadForm<{ asset: Asset }>(
              "/api/media/assets",
              fd,
              (progress) => {
                options?.onProgress?.(progress);
                if (managesProgress) {
                  setUploadProgress(Math.min(99, Math.floor(progress * 100)));
                }
              },
            );
            await loadAssets();
            if (managesProgress) setUploadProgress(100);
            return data.asset;
          },
          options?.successMessage ?? "Upload complete",
        );
        return result.ok ? result.result : null;
      } finally {
        if (managesProgress) {
          await new Promise((resolve) => setTimeout(resolve, 250));
          setUploading(false);
          setUploadProgress(null);
        }
      }
    },
    [currentFolder?.id, loadAssets, runAction],
  );

  const handleDroppedFiles = useCallback(
    async (files: FileList | File[]) => {
      const list = Array.from(files);
      const uploaded: Asset[] = [];
      if (!list.length) return uploaded;

      const weights = list.map((file) => Math.max(file.size, 1));
      const totalBytes = weights.reduce((total, size) => total + size, 0);
      let completedBytes = 0;

      setUploading(true);
      setUploadProgress(0);
      try {
        for (const [index, file] of list.entries()) {
          const asset = await handleDirectUpload(file, {
            onProgress: (fileProgress) => {
              const overallProgress =
                ((completedBytes + fileProgress * weights[index]) /
                  totalBytes) *
                100;
              setUploadProgress(Math.min(99, Math.floor(overallProgress)));
            },
          });
          completedBytes += weights[index];
          if (asset) uploaded.push(asset);
        }
        setUploadProgress(100);
        await new Promise((resolve) => setTimeout(resolve, 250));
      } finally {
        setUploading(false);
        setUploadProgress(null);
      }
      return uploaded;
    },
    [handleDirectUpload],
  );

  const createPresetVariants = useCallback(
    async (assetId: string, presetId: string) => {
      const result = await runAction(
        "upload",
        () =>
          apiJson<{ variants: Asset[] }>(
            `/api/media/assets/${assetId}/variants`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ presetId }),
            },
          ),
        "Variant created",
      );
      if (!result.ok) return null;
      await loadAssets();
      return result.result.variants[0] ?? null;
    },
    [loadAssets, runAction],
  );

  const handlePasteMove = useCallback(async () => {
    if (!pendingMove) return;
    const targetFolderId = currentFolder?.id ?? null;
    await runAction("paste", async () => {
      const folderMoves = pendingMove.folders.map((id) =>
        apiJson(`/api/media/folders/${id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ parentId: targetFolderId }),
        }),
      );
      const assetMoves = pendingMove.assets.map((id) =>
        apiJson(`/api/media/assets/${id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ folderId: targetFolderId }),
        }),
      );
      await Promise.all([...folderMoves, ...assetMoves]);
      setPendingMove(null);
      selection.clearSelection();
      await Promise.all([goToFolder(currentFolder?.id), loadAssets()]);
      setMessage("Moved");
    });
  }, [
    currentFolder?.id,
    goToFolder,
    loadAssets,
    pendingMove,
    runAction,
    selection,
  ]);

  const loadTreeNode = useCallback(
    async (folder: Folder) => {
      setTree((prev) => ({
        ...prev,
        [folder.id]: {
          ...(prev[folder.id] ?? {
            folder,
            childrenIds: [],
            assets: [],
            open: true,
            loaded: false,
          }),
          loading: true,
          open: true,
        },
      }));
      try {
        const [childrenData, assetsData] = await Promise.all([
          apiJson<{ parent: Folder; children: Folder[] }>(
            `/api/media/folders?parentId=${folder.id}`,
          ),
          apiJson<{ assets: Asset[] }>(
            `/api/media/assets?folderId=${folder.id}${includeTrashed ? "&includeTrashed=true" : ""}`,
          ),
        ]);
        setTree((prev) => {
          const next: Record<string, TreeNode> = { ...prev };
          next[folder.id] = {
            folder,
            childrenIds: childrenData.children.map((c) => c.id),
            assets: assetsData.assets ?? [],
            open: true,
            loaded: true,
            loading: false,
          };
          childrenData.children.forEach((child) => {
            const existing = next[child.id];
            next[child.id] = {
              folder: child,
              childrenIds: existing?.childrenIds ?? [],
              assets: existing?.assets ?? [],
              open: existing?.open ?? false,
              loaded: existing?.loaded ?? false,
              loading: existing?.loading ?? false,
            };
          });
          return next;
        });
      } catch (err: any) {
        setMessage(err?.message || "Failed to load tree node");
        setTree((prev) => ({
          ...prev,
          [folder.id]: {
            ...(prev[folder.id] ?? {
              folder,
              childrenIds: [],
              assets: [],
              open: false,
              loaded: false,
            }),
            loading: false,
          },
        }));
      }
    },
    [includeTrashed],
  );

  const toggleNode = useCallback(
    (folder: Folder) => {
      const node = tree[folder.id];
      if (!node?.loaded && !node?.loading) {
        void loadTreeNode(folder);
        return;
      }
      setTree((prev) => ({
        ...prev,
        [folder.id]: {
          ...(prev[folder.id] ?? {
            folder,
            childrenIds: [],
            assets: [] as Asset[],
          }),
          open: !node?.open,
        },
      }));
    },
    [loadTreeNode, tree],
  );

  const openAsset = useCallback((asset: Asset | null) => {
    if (!asset) return;
    const href = buildAssetUrl(asset);
    window.open(href, "_blank", "noopener,noreferrer");
  }, []);

  const downloadAsset = useCallback((asset: Asset | null) => {
    if (!asset) return;
    const href = buildAssetUrl(asset);
    const a = document.createElement("a");
    a.href = `/api/download?url=${encodeURIComponent(href)}&filename=${encodeURIComponent(asset.originalFilename)}`;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, []);

  useEffect(() => {
    void goToFolder();
  }, [goToFolder]);

  useEffect(() => {
    clearSelection();
    void loadAssets();
  }, [clearSelection, currentFolder?.id, includeTrashed, loadAssets]);

  const closeContext = useCallback(() => setContextMenu(null), []);

  return {
    state: {
      currentFolder,
      childFolders,
      assets,
      filteredAssets,
      tree,
      rootId,
      breadcrumbs,
      includeTrashed,
      loadingFolders,
      loadingAssets,
      uploading,
      uploadProgress,
      busyAction,
      viewMode,
      message,
      contextMenu,
      pendingMove,
      inlineFolderName,
      selectedFolderIds: selection.selectedFolderIds,
      selectedAssetIds: selection.selectedAssetIds,
      selectedAsset: selection.selectedAsset,
    },
    actions: {
      goToFolder,
      loadAssets,
      createFolder,
      deleteFolder,
      renameFolder,
      moveFolderToRoot,
      renameAsset,
      updateAssetStatus,
      handleDirectUpload,
      handleDroppedFiles,
      createPresetVariants,
      handlePasteMove,
      toggleNode,
      loadTreeNode,
      openAsset,
      downloadAsset,
      setContextMenu,
      closeContext,
      setIncludeTrashed,
      setViewMode,
      setInlineFolderName,
      setPendingMove,
      setMessage,
      selection,
      getFolderById,
      getAssetById,
    },
  };
}

export type UseMediaManagerReturn = ReturnType<typeof useMediaManager>;
